import {
  PricingLineItem,
  WindowsCurrency,
} from '@cutr/constants/windows/windows';
import { getPositionPricingSummary } from '@cutr/constants/windows/windows-pricing';
import cn from 'classnames';
import { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';

import Card from '@/blocks/Card';
import { InfoMessage } from '@/blocks/InfoMessage';
import AnchorLink from '@/primitives/AnchorLink';
import { TruncatedText } from '@/primitives/TruncatedText';
import { currencyFormatter } from '@/utils/format';

import { OrderLoaderType } from '../queries/order/loaders';
import styles from './QuoteSummary.module.css';

export const QuoteSummary = ({
  children,
  order,
}: PropsWithChildren<{ order: OrderLoaderType }>) => {
  const { t } = useTranslation();

  const currency = order.currency;
  const {
    orderLineItems,
    positionLineItems,
    subtotalAmount,

    discountLineItems,
    totalDiscount,
    subtotalDiscountAmount,

    specialDiscountLineItems,
    totalSpecialDiscount,
    subtotalSpecialDiscountAmount,

    promotionDiscountLineItems,
    totalPromotionDiscount,

    manualQuotingRequired,
    totalAmountExclVAT,
  } = getPositionPricingSummary(order);

  const hasDiscount = totalDiscount !== 0;
  const hasOrderLevelLineItems = orderLineItems.length > 0;

  return (
    <Card style={{ padding: 'var(--space-m)' }}>
      <div className="opposites">
        <h3>{`${t('reseller.quoteSummary.title')} • ${t(
          'reseller.quoteSummary.position',
          {
            count: positionLineItems.length,
          }
        )}`}</h3>
      </div>

      {manualQuotingRequired && (
        <InfoMessage
          variant="warning"
          message={t('reseller.quoteSummary.manualQuoteWarning')}
        />
      )}

      <ul
        className={styles.cardPrice}
        style={{ paddingBottom: children ? 'inherit' : '0' }}
      >
        {hasOrderLevelLineItems && (
          <LineItems
            lineItems={orderLineItems}
            title={t('reseller.quoteSummary.orderLevelLineItems')}
            currency={currency}
          />
        )}

        {positionLineItems.length > 0 && (
          <LineItems
            lineItems={positionLineItems}
            title={t('reseller.quoteSummary.positions')}
            currency={currency}
          />
        )}

        {/* Subtotal of positions */}
        {Boolean(hasDiscount) && (
          <>
            <li className={styles.separator}>
              <strong>{t('reseller.quoteSummary.subtotal')}</strong>
            </li>
            <li className="flexAlign opposites">
              <span className={cn(styles.cell, styles.alignLeft)}>
                {t('reseller.quoteSummary.subtotalOfPositions')}
              </span>
              <span className={cn(styles.cell, styles.alignRight)}>
                {currencyFormatter(subtotalAmount / 100, currency)}
              </span>
            </li>
          </>
        )}

        {/* Discounts */}
        {hasDiscount && (
          <LineItems
            lineItems={discountLineItems}
            title={t('reseller.quoteSummary.discounts')}
            currency={currency}
            onlyTotal={true}
          />
        )}

        {/* Subtotal after discounts */}
        {Boolean(totalSpecialDiscount) && Boolean(totalDiscount) && (
          <>
            <li className={styles.separator}>
              <strong>{t('reseller.quoteSummary.subtotal')}</strong>
            </li>
            <li className="flexAlign opposites">
              <span className={cn(styles.cell, styles.alignLeft)}>
                {t('reseller.quoteSummary.subtotalAfterDiscount')}
              </span>
              <span className={cn(styles.cell, styles.alignRight)}>
                {currencyFormatter(subtotalDiscountAmount / 100, currency)}
              </span>
            </li>
          </>
        )}

        {/* Special discounts */}
        {Boolean(totalSpecialDiscount) && (
          <>
            <LineItems
              lineItems={specialDiscountLineItems}
              title={t('reseller.quoteSummary.discounts')}
              currency={currency}
              onlyTotal={true}
            />
          </>
        )}

        {Boolean(totalPromotionDiscount) &&
          Boolean(totalDiscount || totalSpecialDiscount) && (
            <>
              <li className={styles.separator}>
                <strong>{t('reseller.quoteSummary.subtotal')}</strong>
              </li>
              <li className="flexAlign opposites">
                <span className={cn(styles.cell, styles.alignLeft)}>
                  {t('reseller.quoteSummary.subtotalAfterDiscount')}
                </span>
                <span className={cn(styles.cell, styles.alignRight)}>
                  {currencyFormatter(
                    subtotalSpecialDiscountAmount / 100,
                    currency
                  )}
                </span>
              </li>
            </>
          )}

        {Boolean(totalPromotionDiscount) && (
          <LineItems
            lineItems={promotionDiscountLineItems}
            title={t('reseller.quoteSummary.discounts')}
            currency={currency}
            onlyTotal={true}
          />
        )}

        <li className={cn(styles.bolder, styles.separator, styles.total)}>
          <strong>{t('reseller.quoteSummary.totalExcludingVAT')}</strong>
          <strong className={cn(styles.amount)}>
            {manualQuotingRequired && t('reseller.quoteSummary.minimum') + ' '}
            {currencyFormatter(totalAmountExclVAT / 100, currency)}
          </strong>
        </li>
      </ul>

      {children}
    </Card>
  );
};

const LineItems = ({
  lineItems,
  title,
  currency,
  onlyTotal = false,
}: {
  lineItems: PricingLineItem[];
  title: string;
  currency: WindowsCurrency;
  onlyTotal?: boolean;
}) => {
  return (
    <>
      <li className={styles.separator}>
        <strong>{title}</strong>
      </li>
      {lineItems.map((lineItem, index) => {
        const { quantity, unitAmount, totalAmount } = lineItem;
        return (
          <li key={title + '-' + index} className={styles.grid}>
            <Description lineItem={lineItem} />
            {!onlyTotal && (
              <>
                <span className={cn(styles.cell, styles.alignRight)}>
                  {quantity}
                </span>

                <span className={cn(styles.cell, styles.alignCenter)}>
                  {'×'}
                </span>
                <span className={cn(styles.cell, styles.alignRight)}>
                  {unitAmount
                    ? currencyFormatter(unitAmount / 100, currency)
                    : '-'}
                </span>
                <span className={cn(styles.cell, styles.alignCenter)}>
                  {'='}
                </span>
              </>
            )}
            {onlyTotal && (
              <>
                <span className={cn(styles.cell)}></span>
                <span className={cn(styles.cell)}></span>
                <span className={cn(styles.cell)}></span>
                <span className={cn(styles.cell)}></span>
              </>
            )}
            <span className={cn(styles.cell, styles.alignRight)}>
              {totalAmount
                ? currencyFormatter(totalAmount / 100, currency)
                : '-'}
            </span>
          </li>
        );
      })}
    </>
  );
};

const Description = ({ lineItem }: { lineItem: PricingLineItem }) => {
  const { t } = useTranslation();
  const { positionId, description, manualQuotingRequired } = lineItem;
  if (positionId) {
    return (
      <div className={styles.cell}>
        <AnchorLink anchor={`position-summary-${positionId}`}>
          <TruncatedText text={description} maxLength={30} />
        </AnchorLink>
        {manualQuotingRequired && (
          <>
            <span className={styles.manuallyQuoted}>
              {t('cutlist-summary.manualQuote')}
            </span>
          </>
        )}
      </div>
    );
  }
  return (
    <div className={styles.cell}>
      {description}
      {manualQuotingRequired && (
        <>
          <span className={styles.manuallyQuoted}>
            {t('cutlist-summary.manualQuote')}
          </span>
        </>
      )}
    </div>
  );
};
